import type { SandboxMemorySnapshot } from '@wegic/site-sdk/shared';
import { APP_ENV } from '@/const/env';
import { collect } from '@/modules/Collect';
import { useChatStore } from '@/stores/chat';
import { getChatLastMessageId } from '@/stores/chat/helper';
import { usePreviewStore } from '@/stores/preview';
import { useProjectStore } from '@/stores/project';
import {
  BYTES_PER_MB,
  type DomSnapshot,
  type JsHeapSnapshot,
  MEMORY_SAMPLE_EVENT,
  type MemorySampleAnchor,
  type MemorySampleContext,
} from './const';

/** 一次内存读数 + 时间戳，用于计算相邻两次的涨幅 */
export interface HeapReading {
  usedJsHeap: number;
  at: number;
}

/**
 * 计算相邻两次读数的涨幅（MB/min），纯函数，方便单测。
 * 返回 null 表示无法计算（缺前一次读数、时间差非正、或当前无 heap 数据）。
 */
export function computeGrowthMbPerMin(
  prev: HeapReading | null,
  curr: HeapReading | null,
): number | null {
  if (!prev || !curr) {
    return null;
  }

  const deltaMs = curr.at - prev.at;
  if (deltaMs <= 0) {
    return null;
  }

  const deltaMb = (curr.usedJsHeap - prev.usedJsHeap) / BYTES_PER_MB;
  return (deltaMb / deltaMs) * 60_000;
}

/** 读取 JS heap。Chromium only；其他引擎返回 null，上报时透传，数仓过滤即可。 */
export function readJsHeap(): JsHeapSnapshot | null {
  const mem = performance.memory;
  if (!mem) {
    return null;
  }

  return {
    usedJsHeap: mem.usedJSHeapSize,
    totalJsHeap: mem.totalJSHeapSize,
    jsHeapLimit: mem.jsHeapSizeLimit,
  };
}

/** 读取 DOM 计数快照。detached DOM 和残留 canvas 的代理指标。 */
export function readDomSnapshot(): DomSnapshot {
  return {
    domNodes: document.getElementsByTagName('*').length,
    domIframes: document.getElementsByTagName('iframe').length,
    domCanvases: document.getElementsByTagName('canvas').length,
  };
}

/**
 * 从各 store 读取当前动作上下文，只读不写，无副作用。
 * @param anchor 采样锚点
 * @param sessionRound session 内新增轮次（由 MemoryMonitor 维护），与历史消息解耦。
 *   不传则退回读 chatStore.getCurrentRoundNumber()（历史累计值，仅作兜底）。
 */
export function readSampleContext(anchor: MemorySampleAnchor, sessionRound?: number): MemorySampleContext {
  const chatState = useChatStore.getState();
  const previewState = usePreviewStore.getState();
  const projectState = useProjectStore.getState();

  return {
    anchor,
    projectId: projectState.projectId,
    messageId: getChatLastMessageId(),
    // 优先使用 session 级计数（从 0 开始，不受历史消息影响）
    roundIndex: sessionRound ?? chatState.getCurrentRoundNumber(),
    targetLayer: previewState.targetLayer,
    sandboxRuntimeStatus: previewState.sandboxRuntimeStatus,
    appEnv: APP_ENV,
  };
}

/**
 * 采集并上报一条内存样本。
 *
 * customProperties 只包含"内存数值 + 动作上下文"，
 * sessionId / userId / browser / device 等由 CollectSDK.getRequestData 自动注入，
 * 不重复上报。
 */
export function reportMemorySample(
  anchor: MemorySampleAnchor,
  extra?: Record<string, unknown>,
  sandbox?: SandboxMemorySnapshot | null,
  sessionRound?: number,
  sandboxPull?: {
    status: string;
    durationMs: number;
    error?: string;
  },
): void {
  const heap = readJsHeap();
  const dom = readDomSnapshot();
  const ctx = readSampleContext(anchor, sessionRound);

  collect(MEMORY_SAMPLE_EVENT, {
    // ── 锚点与上下文 ──────────────────────────────
    anchor: ctx.anchor,
    project_id: ctx.projectId,
    message_id: ctx.messageId,
    round_index: ctx.roundIndex,
    active_preview_source: ctx.targetLayer === 'live' ? 'sandbox' : 'pre-release',
    sandbox_liveness: ctx.sandboxRuntimeStatus,
    app_env: ctx.appEnv,

    // ── JS Heap（主 frame，Chromium only；null 时数仓侧忽略即可）──
    used_js_heap: heap?.usedJsHeap ?? null,
    total_js_heap: heap?.totalJsHeap ?? null,
    js_heap_limit: heap?.jsHeapLimit ?? null,

    // ── DOM 计数（detached DOM / canvas 泄漏代理）───────────────
    dom_nodes: dom.domNodes,
    dom_iframes: dom.domIframes,
    dom_canvases: dom.domCanvases,

    // ── sandbox 进程内存（Phase 2，父级 RPC 拉取；null = 未拉到/非 sandbox 场景）──
    // 与主 frame 同一时刻采集，数仓侧可直接对比「内存涨在主进程还是 sandbox 进程」。
    sandbox_used_js_heap: sandbox?.usedJsHeap ?? null,
    sandbox_total_js_heap: sandbox?.totalJsHeap ?? null,
    sandbox_js_heap_limit: sandbox?.jsHeapLimit ?? null,
    sandbox_dom_nodes: sandbox?.domNodes ?? null,
    sandbox_dom_images: sandbox?.domImages ?? null,
    sandbox_dom_canvases: sandbox?.domCanvases ?? null,
    sandbox_dom_scripts: sandbox?.domScripts ?? null,
    sandbox_estimated_image_bitmap_bytes: sandbox?.estimatedImageBitmapBytes ?? null,
    sandbox_largest_image_bitmap_bytes: sandbox?.largestImageBitmapBytes ?? null,
    sandbox_estimated_canvas_bytes: sandbox?.estimatedCanvasBytes ?? null,
    sandbox_visibility_state: sandbox?.documentVisibilityState ?? null,
    sandbox_was_discarded: sandbox?.documentWasDiscarded ?? null,
    sandbox_long_task_count: sandbox?.longTaskCount ?? null,
    sandbox_long_task_total_ms: sandbox?.longTaskTotalMs ?? null,
    sandbox_long_task_max_ms: sandbox?.longTaskMaxMs ?? null,
    sandbox_event_loop_lag_ms: sandbox?.eventLoopLagMs ?? null,
    sandbox_event_loop_lag_max_ms: sandbox?.eventLoopLagMaxMs ?? null,
    sandbox_pull_status: sandboxPull?.status ?? 'not_attempted',
    sandbox_pull_duration_ms: sandboxPull?.durationMs ?? null,
    sandbox_pull_error: sandboxPull?.error ?? null,

    // ── 锚点专属附加维度（如 growth_anomaly 的实测涨幅）──────────
    ...extra,
  });
}
